const getCoaRecordTimestamp = (record: any): number => {
  const raw = record?.created ?? record?.Created ?? record?.createdDateTime ?? record?.CreatedDateTime;
  if (!raw) {
    return Number.NEGATIVE_INFINITY;
  }

  const parsed = Date.parse(String(raw));
  return Number.isNaN(parsed) ? Number.NEGATIVE_INFINITY : parsed;
};

const getCoaRecordId = (record: any): number => {
  const raw = record?.id ?? record?.Id ?? 0;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : 0;
};

export function sortCoaResponseRecordsNewestFirst<T = any>(rawResp: T[] | unknown): T[] {
  if (!Array.isArray(rawResp)) {
    return [];
  }

  return [...rawResp].sort((a: any, b: any) => {
    const createdA = getCoaRecordTimestamp(a);
    const createdB = getCoaRecordTimestamp(b);
    const hasCreatedA = Number.isFinite(createdA);
    const hasCreatedB = Number.isFinite(createdB);
    if (hasCreatedA && hasCreatedB && createdA !== createdB) {
      return createdB - createdA;
    }
    if (hasCreatedA !== hasCreatedB) {
      return hasCreatedA ? -1 : 1;
    }

    return getCoaRecordId(b) - getCoaRecordId(a);
  });
}

export function getLatestCoaResponseRecord<T = any>(rawResp: T[] | T | null | undefined): T | null {
  return Array.isArray(rawResp) ? sortCoaResponseRecordsNewestFirst<T>(rawResp)[0] ?? null : rawResp ?? null;
}

export function getLatestCoaPayload<T = any>(rawResp: T[] | T | null | undefined): T | null {
  return decodeCoaRecord(getLatestCoaResponseRecord(rawResp)) as T | null;
}

const decodeCoaRecord = (record: any): any => {
  if (!record || typeof record !== 'object' || Array.isArray(record)) return null;
  const { result, Result, ...outer } = record;
  const hasResult = Object.prototype.hasOwnProperty.call(record, 'result');
  if (!hasResult && !Object.prototype.hasOwnProperty.call(record, 'Result')) return record;
  let payload = hasResult ? result : Result;
  while (typeof payload === 'string') {
    try { payload = JSON.parse(payload); } catch { return outer; }
  }
  return payload && typeof payload === 'object' && !Array.isArray(payload) ? { ...payload, ...outer } : outer;
};

// Editable package flows need manual ownership from the canonical COA. Keep
// historical/download consumers on the separate chronological selector above.
export function getEditableCoaResponseRecord<T = any>(rawResp: T[] | T | null | undefined): T | null {
  if (!Array.isArray(rawResp)) return rawResp ?? null;
  const candidates = rawResp.map((record) => ({ record, metadata: decodeCoaRecord(record) }))
    .filter(({ metadata }) => metadata && (Object.prototype.hasOwnProperty.call(metadata, 'printRunId')
      ? metadata.printRunId === null : Object.prototype.hasOwnProperty.call(metadata, 'PrintRunId') && metadata.PrintRunId === null));
  const metadata = sortCoaResponseRecordsNewestFirst(candidates.map((candidate) => candidate.metadata))[0];
  return candidates.find((candidate) => candidate.metadata === metadata)?.record ?? getLatestCoaResponseRecord(rawResp);
}

export function getEditableCoaPayload<T = any>(rawResp: T[] | T | null | undefined): T | null {
  return decodeCoaRecord(getEditableCoaResponseRecord(rawResp)) as T | null;
}
